import React, { useState, useEffect } from 'react';
import './Login.css';
import logo from './logo.png';
import { apiFetch } from './api';

function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [loading, setLoading] = useState(false);

  // Şifremi unuttum state
  const [forgotStep, setForgotStep] = useState(null); // null | 'email' | 'code' | 'newpass'
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');
  const [codeTimer, setCodeTimer] = useState(120);

  const DOMAIN = '@kocaelisaglik.edu.tr';

  // Kod geri sayım sayacı
  useEffect(() => {
    if (forgotStep !== 'code') return;
    setCodeTimer(120);
    const t = setInterval(() => {
      setCodeTimer(prev => {
        if (prev <= 1) { clearInterval(t); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [forgotStep]);

  const formatTimer = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m} dakika ${sec.toString().padStart(2, '0')} saniye`;
  };

  const closeForgot = () => {
    setForgotStep(null);
    setResetEmail('');
    setResetCode('');
    setNewPassword('');
    setNewPasswordConfirm('');
    setResetError('');
    setResetSuccess('');
  };

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetLoading(true);
    const fullEmail = resetEmail.includes('@') ? resetEmail : resetEmail.trim() + DOMAIN;
    try {
      const res = await apiFetch('/auth/student/request-reset-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: fullEmail }),
      });
      const data = await res.json();
      if (data.success) {
        setForgotStep('code');
      } else {
        setResetError(data.message || 'Bir hata oluştu');
      }
    } catch {
      setResetError('Bağlantı hatası. Lütfen tekrar deneyin.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (codeTimer === 0) { setResetError('Kodun süresi doldu. Lütfen yeni kod isteyin.'); return; }
    setResetError('');
    setResetLoading(true);
    const fullEmail = resetEmail.includes('@') ? resetEmail : resetEmail.trim() + DOMAIN;
    try {
      const res = await apiFetch('/auth/student/verify-reset-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: fullEmail, code: resetCode }),
      });
      const data = await res.json();
      if (data.success) {
        setForgotStep('newpass');
        setResetError('');
      } else {
        setResetError(data.message || 'Geçersiz kod');
      }
    } catch {
      setResetError('Bağlantı hatası. Lütfen tekrar deneyin.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError('');
    if (newPassword !== newPasswordConfirm) { setResetError('Şifreler eşleşmiyor'); return; }
    if (newPassword.length < 6) { setResetError('Şifre en az 6 karakter olmalıdır'); return; }
    setResetLoading(true);
    const fullEmail = resetEmail.includes('@') ? resetEmail : resetEmail.trim() + DOMAIN;
    try {
      const res = await apiFetch('/auth/student/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: fullEmail, code: resetCode, new_password: newPassword }),
      });
      const data = await res.json();
      if (data.success) {
        setResetSuccess('Şifreniz başarıyla güncellendi. Giriş yapabilirsiniz.');
        setForgotStep(null);
        setResetEmail('');
        setResetCode('');
        setNewPassword('');
        setNewPasswordConfirm('');
      } else {
        setResetError(data.message || 'Bir hata oluştu');
      }
    } catch {
      setResetError('Bağlantı hatası. Lütfen tekrar deneyin.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrorCode('');
    setLoading(true);

    const fullEmail = username.includes('@') ? username : username.trim() + DOMAIN;

    try {
      const response = await apiFetch('/login', {
        method: 'POST',
        body: JSON.stringify({ username: fullEmail, password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        if (onLogin) {
          onLogin({ userType: data.user.userType, user: data.user, token: data.token });
        }
      } else {
        setError(data.message || 'Giriş başarısız. Lütfen bilgilerinizi kontrol edin.');
        setErrorCode(data.code || '');
      }
    } catch (err) {
      setError('Bağlantı hatası. Lütfen tekrar deneyin.');
      setErrorCode('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          <img src={logo} alt="KOSTÜ Logo" className="login-logo" />
          <h1>KOSTÜ Sınav Modülü</h1>
          <p>Hesabınıza giriş yapın</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="username">E-posta</label>
            <div className="email-input-wrapper">
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value.replace(/@.*/, ''))}
                placeholder="kullanici.adi"
                required
                autoComplete="username"
              />
              <span className="email-domain">@kocaelisaglik.edu.tr</span>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Şifre</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Şifrenizi girin"
              required
              autoComplete="current-password"
            />
          </div>

          {error && (
            <div className={`error-message ${errorCode === 'STUDENT_NOT_IN_ACTIVE_YEAR' ? 'student-registration-error' : ''}`}>
              {errorCode === 'STUDENT_NOT_IN_ACTIVE_YEAR' ? (
                <>
                  <strong>Aktif öğrenci kaydınız bulunamadı.</strong>
                  <span>
                    Aktif akademik yıl öğrenci listesinde kaydınız bulunmamaktadır.
                    {' '}Kaydınızın sisteme eklenmesi için sınavına gireceğiniz dersin
                    {' '}sorumlu akademisyeniyle iletişime geçiniz.
                  </span>
                </>
              ) : error}
            </div>
          )}
          {resetSuccess && <div className="success-message">{resetSuccess}</div>}

          <button type="submit" className="login-button" disabled={loading}>
            {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
          </button>
        </form>

        <div className="forgot-divider" />
        <button type="button" className="forgot-password-btn" onClick={() => { setForgotStep('email'); setResetSuccess(''); setResetError(''); }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>
          Şifremi Unuttum
        </button>
      </div>

      {/* Şifremi Unuttum Modal */}
      {forgotStep && (
        <div className="forgot-overlay" onClick={(e) => { if (e.target.classList.contains('forgot-overlay')) closeForgot(); }}>
          <div className="forgot-modal">
            <button className="forgot-close" onClick={closeForgot}>×</button>

            {/* Adım 1: E-posta */}
            {forgotStep === 'email' && (
              <>
                <h3>Şifremi Unuttum</h3>
                <p className="forgot-desc">E-posta adresinizi girin, doğrulama kodu gönderilecektir.</p>
                <form onSubmit={handleRequestCode}>
                  <div className="form-group">
                    <label>E-posta Adresi</label>
                    <div className="email-input-wrapper">
                      <input
                        type="text"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value.replace(/@.*/, ''))}
                        placeholder="kullanici.adi"
                        required
                        autoFocus
                      />
                      <span className="email-domain">@kocaelisaglik.edu.tr</span>
                    </div>
                  </div>
                  {resetError && <div className="error-message">{resetError}</div>}
                  <button type="submit" className="login-button" disabled={resetLoading}>
                    {resetLoading ? 'Gönderiliyor...' : 'Kod Gönder'}
                  </button>
                </form>
              </>
            )}

            {/* Adım 2: Kod Doğrulama */}
            {forgotStep === 'code' && (
              <>
                <h3>Doğrulama Kodu</h3>
                <p className="forgot-desc forgot-info">
                  E-posta adresiniz sistemde kayıtlı ve aktif akademik yıl öğrenci
                  listesinde bulunuyorsa doğrulama kodu gönderilmiştir.
                </p>
                <form onSubmit={handleVerifyCode}>
                  <div className="form-group">
                    <label>Doğrulama Kodu</label>
                    <input
                      type="text"
                      value={resetCode}
                      onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="6 haneli kodu girin"
                      maxLength={6}
                      required
                      autoFocus
                      className="code-input"
                    />
                    <p className={`code-timer ${codeTimer <= 30 ? 'code-timer-warning' : ''}`}>
                      Kalan Süre: {formatTimer(codeTimer)}
                    </p>
                  </div>
                  {resetError && <div className="error-message">{resetError}</div>}
                  <button type="submit" className="login-button" disabled={resetLoading || codeTimer === 0}>
                    {resetLoading ? 'Doğrulanıyor...' : 'Doğrula'}
                  </button>
                  <button type="button" className="forgot-back-btn" onClick={() => { setForgotStep('email'); setResetError(''); setResetCode(''); }}>
                    Geri Dön
                  </button>
                </form>
              </>
            )}

            {/* Adım 3: Yeni Şifre */}
            {forgotStep === 'newpass' && (
              <>
                <h3>Yeni Şifre Belirle</h3>
                <p className="forgot-desc forgot-info">Kod doğrulandı. Lütfen yeni şifrenizi belirleyin.</p>
                <form onSubmit={handleResetPassword}>
                  <div className="form-group">
                    <label>Yeni Şifre</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Yeni şifrenizi girin"
                      required
                      autoFocus
                    />
                  </div>
                  <div className="form-group">
                    <label>Yeni Şifre (Tekrar)</label>
                    <input
                      type="password"
                      value={newPasswordConfirm}
                      onChange={(e) => setNewPasswordConfirm(e.target.value)}
                      placeholder="Yeni şifrenizi tekrar girin"
                      required
                    />
                  </div>
                  {resetError && <div className="error-message">{resetError}</div>}
                  <button type="submit" className="login-button" disabled={resetLoading}>
                    {resetLoading ? 'Güncelleniyor...' : 'Şifreyi Güncelle ve Giriş Yap'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;
